import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { apiGetUsage } from '../api/backend.js';
import { formatBytes, daysLeft } from '../utils/format.js';
import { Link } from 'react-router-dom';
import './Dashboard.css';

async function pingOnce(url) {
  const t0 = performance.now();
  try {
    await fetch(url, { cache: 'no-store', mode: 'no-cors' });
  } catch (_) {
  }
  return Math.round(performance.now() - t0);
}

function useSpeedTest() {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [latency, setLatency] = useState(null);
  const [downMbps, setDownMbps] = useState(null);
  const [error, setError] = useState(null);
  const ctrlRef = useRef(null);

  const run = useCallback(async () => {
    if (running) return;
    setError(null);
    setDownMbps(null);
    setLatency(null);
    setProgress(0);
    setRunning(true);

    try {
      const PING_URL = 'https://www.google.com/generate_204';
      const tries = 3;
      let sum = 0;
      for (let i = 0; i < tries; i++) sum += await pingOnce(PING_URL);
      setLatency(Math.round(sum / tries));

      const SOURCES = [
        { url: `https://speed.cloudflare.com/__down?bytes=${20 * 1024 * 1024}&ts=${Date.now()}`, size: 20 * 1024 * 1024 },
        { url: `https://speed.cloudflare.com/__down?bytes=${10 * 1024 * 1024}&ts=${Date.now()}`, size: 10 * 1024 * 1024 },
      ];

      let measured = null;
      for (const s of SOURCES) {
        const controller = new AbortController();
        ctrlRef.current = controller;
        const t0 = performance.now();
        const res = await fetch(s.url, { cache: 'no-store', signal: controller.signal });
        if (!res.ok || !res.body) throw new Error('Источник скорости недоступен');

        const reader = res.body.getReader();
        let received = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          received += value.byteLength;
          setProgress(Math.min(1, received / s.size));
        }
        const dt = (performance.now() - t0) / 1000;
        measured = +( (received * 8 / dt) / 1e6 ).toFixed(2);
        break;
      }
      if (measured == null) throw new Error('Не удалось измерить скорость');
      setDownMbps(measured);
      setProgress(1);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setRunning(false);
      ctrlRef.current = null;
    }
  }, [running]);

  const cancel = useCallback(() => {
    ctrlRef.current?.abort();
    ctrlRef.current = null;
    setRunning(false);
  }, []);

  return { running, progress, latency, downMbps, error, run, cancel };
}

export default function Dashboard({ user }) {
  const [usage, setUsage] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setErr(null);
        const now = new Date();
        const to = now.toISOString();
        const from = new Date(now.getTime() - 30 * 24 * 3600 * 1000).toISOString();
        const data = await apiGetUsage(user.uuid, from, to);
        if (!cancel) setUsage(data);
      } catch (e) {
        if (!cancel) setErr(String(e.message || e));
      }
    })();
    return () => { cancel = true; };
  }, [user.uuid]);

  const usedTotal = useMemo(() => {
    return Number(user?.userTraffic?.lifetimeUsedTrafficBytes || 0);
  }, [user]);

  const leftSafe = (() => {
    const n = Number(daysLeft(user.expireAt));
    return isNaN(n) ? 0 : Math.max(0, n);
  })();

  const ringCapDays = 30;
  const progress = Math.min(1, leftSafe / ringCapDays); // 0..1
  const startDeg = -50;
  const connected = user.devicesConnected ?? 0;

  const speed = useSpeedTest();

  return (
    <>
      <section className="dash-hero">
        <h1 className="dash-title">
          Добро пожаловать,<br />
          <span className="dash-username">@{user.username}</span>
        </h1>
      </section>

      <section className="dash-progress">
        <div className="donut"
             style={{ ['--progress']: progress, ['--start']: `${startDeg}deg` }}>
          <div className="donut-inner">
            <div className="ring-caption">Оплачено дней:</div>
            <div className="ring-days">{leftSafe}</div>
          </div>
        </div>
      </section>

      <section className="dash-cards">
        <article className="stat-card">
          <div className="icon">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M4 6h16v9H4z" fill="none" stroke="white" strokeWidth="1.6"/>
              <path d="M2 18h20" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="stat-title">Устройств онлайн</div>
          <div className="stat-value">{connected}</div>
        </article>

        <article className="stat-card">
          <div className="icon">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <circle cx="6" cy="6" r="1.8" fill="white"/><circle cx="12" cy="6" r="1.8" fill="white"/>
              <circle cx="18" cy="6" r="1.8" fill="white"/><circle cx="6" cy="12" r="1.8" fill="white"/>
              <circle cx="12" cy="12" r="1.8" fill="white"/><circle cx="18" cy="12" r="1.8" fill="white"/>
              <circle cx="6" cy="18" r="1.8" fill="white"/><circle cx="12" cy="18" r="1.8" fill="white"/>
              <circle cx="18" cy="18" r="1.8" fill="white"/>
            </svg>
          </div>
          <div className="stat-title">Трафик (30 дней)</div>
          <div className="stat-value">{formatBytes(usedTotal)}</div>
        </article>

        <article className="stat-card speed-card">
          <div className="icon">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M12 4a8 8 0 0 0-8 8v3a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-3a8 8 0 0 0-8-8Z" fill="none" stroke="white" strokeWidth="1.6"/>
              <path d="M12 12l6-3" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="stat-title">Скорость подключения</div>

          {!speed.running && speed.downMbps == null && (
            <>
              <div className="speed-muted">Нажми, чтобы измерить пинг и загрузку</div>
              <button className="btn" onClick={speed.run}>Измерить</button>
              {speed.error && <div className="small-err">{speed.error}</div>}
            </>
          )}

          {speed.running && (
            <>
              <div className="speed-row">
                <div className="speed-label">Тест загрузки…</div>
                <div className="speed-progress">
                  <div className="speed-bar" style={{ width: `${Math.round(speed.progress * 100)}%` }} />
                </div>
              </div>
              <button className="btn btn-secondary" onClick={speed.cancel}>Отмена</button>
            </>
          )}

          {!speed.running && speed.downMbps != null && (
            <div className="speed-results">
              <div className="speed-pill">
                <div className="pill-label">Загрузка</div>
                <div className="pill-value">{speed.downMbps} Mbps</div>
              </div>
              {speed.latency != null && (
                <div className="speed-pill">
                  <div className="pill-label">Пинг</div>
                  <div className="pill-value">{speed.latency} ms</div>
                </div>
              )}
              <button className="btn" onClick={speed.run}>Повторить</button>
              {speed.error && <div className="small-err">{speed.error}</div>}
            </div>
          )}
        </article>


      </section>

      {err && <div className="dash-error">{err}</div>}
    </>
  );
}
